'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ComparisonFeature {
  feature: string;
  traditional: string;
  revive: string;
  advantage: string;
}

const SolutionOverview = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeView, setActiveView] = useState<'traditional' | 'revive'>('traditional');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const comparisonFeatures: ComparisonFeature[] = [
  {
    feature: 'Treatment Efficacy',
    traditional: '32% complication reduction',
    revive: '68% complication reduction',
    advantage: '+36% improvement'
  },
  {
    feature: 'Recovery Time',
    traditional: '14-21 days average',
    revive: '8-12 days average',
    advantage: '40% faster'
  },
  {
    feature: 'Monitoring Capability',
    traditional: 'Manual periodic checks',
    revive: 'Real-time continuous monitoring',
    advantage: '24/7 precision'
  },
  {
    feature: 'Rectal Probe slip detection',
    traditional: 'No detection',
    revive: 'Automatic detection of slip',
    advantage: 'Safe, uninterrupted treatment'
  }];


  const technologyFeatures = [
  {
    icon: 'CpuChipIcon',
    title: 'Integrated Brain Monitoring',
    description: 'World\'s first system combining whole-body cooling with continuous built-in aEEG monitoring—eliminating multiple external monitors and reducing complexity, cost, and potential errors.'
  },
  {
    icon: 'ShieldCheckIcon',
    title: 'Smart Automated Feedback Loop',
    description: 'Real-time feedback from baby\'s vitals automatically adjusts cooling process, ensuring target therapeutic temperature is reached quickly and maintained safely while reducing NICU staff burden.'
  },
  {
    icon: 'ChartBarIcon',
    title: 'Resource-Constrained Design',
    description: 'Tailored for India and LMIC hospitals—cost-effective, portable, intuitive with built-in safeguards ensuring reliable operation even with inconsistent infrastructure or minimal training.'
  },
  {
    icon: 'BeakerIcon',
    title: 'Data-Driven & Future-Ready',
    description: 'Built with AI integration in mind—vitals data supports algorithms for early detection of seizures or abnormal brain activity, setting the stage for comprehensive neonatal brain care platform.'
  }];

  const clinicalImages = [
  {
    src: '/assets/images/Revive_in_Hospital_5-1766903195945.jpeg',
    alt: 'REVIVE whole body cooling device by Sensivision Health Technologies Pvt. Ltd. in actual hospital use, showing integrated brain monitoring with real-time temperature control at 36.2°C for treating Hypoxic Ischemic Encephalopathy in newborns'
  },
  {
    src: '/assets/images/Revive_in_Hospital_1-1766903240466.jpg',
    alt: 'Hospital NICU setting showing newborn baby receiving whole body cooling therapy on blue REVIVE cooling blanket system, white and blue REVIVE control unit with black touchscreen display showing 92.4°C temperature reading, multiple monitoring sensors and cables connected to patient, professional hospital environment with medical equipment in background'
  },
  {
    src: '/assets/images/Revive_in_Hospital_2_1_-1767246582793.jpg',
    alt: 'Real hospital NICU setting showing newborn baby receiving treatment with REVIVE cooling system (blue cooling blanket/mattress), surrounded by comprehensive monitoring equipment including vital signs monitors, ventilator, and various medical devices. Professional hospital environment with bed number 65 visible, showcasing actual clinical implementation of the REVIVE technology'
  },
  {
    src: '/assets/images/Revive_in_Hospital_3-1767246643114.jpg',
    alt: 'Clinical REVIVE implementation showing newborn baby on cooling system with clear blue cooling mattress, prominent touchscreen display showing 33.5°C temperature reading, multiple monitoring parameters, REVIVE branding with Sensivision logo, hospital NICU environment with medical equipment and supplies visible in background'
  }];



  return (
    <section id="solution-overview" className="py-24 bg-surface relative">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16 relative">
          {/* Subtle background image */}
          <div className="absolute inset-0 -mx-6 -mt-12 rounded-3xl overflow-hidden opacity-[0.25]">
            <AppImage
              src="/assets/images/RevPicture_1-1767249801678.png"
              alt="REVIVE device interface background"
              className="w-full h-full object-contain object-right-top scale-110" />

          </div>
          
          {/* Content with relative positioning to appear above background */}
          <div className="relative z-10">
            <div className="inline-flex items-center space-x-2 bg-neuro-primary/10 text-neuro-primary px-4 py-2 rounded-2xl mb-6">
              <Icon name="BeakerIcon" size={20} />
              <span className="text-sm font-semibold">Breakthrough Innovation</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold font-heading text-foreground mb-6">
              How REVIVE Transforms Neonatal Care
            </h2>

            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              The world's first integrated solution combining whole-body cooling with continuous brain monitoring—designed specifically for resource-constrained settings.
            </p>
          </div>
        </div>

        {/* REVIVE Product Video Section */}
        <div className="mb-20">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-neuro-primary/5 to-primary/5 rounded-3xl p-8 lg:p-12 shadow-card">
              <div className="text-center mb-8">
                <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-2xl mb-4">
                  <Icon name="PlayIcon" size={20} />
                  <span className="text-sm font-semibold">See REVIVE in Action</span>
                </div>
                <h3 className="text-3xl font-bold font-heading text-foreground mb-3">
                  Watch How REVIVE Saves Lives
                </h3>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Discover how our breakthrough technology is revolutionizing neonatal care across India
                </p>
              </div>

              {/* YouTube Video Embed */}
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-2xl shadow-lg"
                  src="https://www.youtube.com/embed/-kPtia8acbc"
                  title="REVIVE Product Video - Sensivision Health Technologies"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    border: 'none'
                  }} />
                
              </div>

              {/* Video Stats */}
              <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-border/50">
                <div className="text-center">
                  <div className="text-2xl font-bold text-neuro-primary mb-1">40+</div>
                  <div className="text-sm text-muted-foreground">Hospital Partners</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-neuro-primary mb-1">68%</div>
                  <div className="text-sm text-muted-foreground">Better Outcomes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-neuro-primary mb-1">24/7</div>
                  <div className="text-sm text-muted-foreground">Brain Monitoring</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Traditional vs REVIVE Comparison */}
        <div className="bg-card rounded-2xl shadow-card overflow-hidden mb-12 border-2 border-nicu-vital/30">
          <div className="bg-gradient-to-r from-nicu-vital/10 to-nicu-vital/5 p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-nicu-vital/20 rounded-full brain-activity-glow">
                <Icon name="SparklesIcon" size={32} className="text-nicu-vital" />
              </div>
              <h3 className="font-bold font-heading text-foreground text-[32px]">
                What is Unique About REVIVE
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="grid grid-cols-2 gap-4">
                {clinicalImages.map((image, idx) =>
                <div key={`clinical-image-${idx}`} className="relative h-48 rounded-2xl overflow-hidden brain-activity-glow shadow-lg">
                    <AppImage
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover" />

                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="bg-success/10 border-l-4 border-success p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Integrated Brain Monitoring</h4>
                      <p className="text-muted-foreground text-sm">World's first whole body cooling with built-in continuous aEEG monitoring</p>
                    </div>
                  </div>
                </div>

                <div className="bg-success/10 border-l-4 border-success p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Real-Time Neurological Assessment</h4>
                      <p className="text-muted-foreground text-sm">Continuous brain activity tracking during treatment for proactive intervention</p>
                    </div>
                  </div>
                </div>

                <div className="bg-success/10 border-l-4 border-success p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Superior Clinical Outcomes</h4>
                      <p className="text-muted-foreground text-sm">68% complication reduction with 40% faster recovery (8-12 days)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-success/10 border-l-4 border-success p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Automated Smart Feedback</h4>
                      <p className="text-muted-foreground text-sm">Real-time vitals automatically adjust cooling, reducing staff burden and errors</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Traditional Cooling Methods Section - Moved After REVIVE Unique Section */}
        <div className="bg-card rounded-2xl shadow-card overflow-hidden mb-16 border-2 border-error/30">
          <div className="bg-gradient-to-r from-error/10 to-error/5 p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-error/20 rounded-full">
                <Icon name="ExclamationTriangleIcon" size={32} className="text-error" />
              </div>
              <h3 className="text-3xl font-bold font-heading text-foreground">
                Traditional Cooling Methods: Critical Limitations
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative h-80 md:h-full rounded-2xl overflow-hidden shadow-lg">
                <AppImage
                  src="https://images.unsplash.com/photo-1634147070282-40cbdd54670a"
                  alt="Traditional neonatal care showing manual monitoring equipment and basic incubator setup in hospital NICU"
                  className="w-full h-full object-cover" />
              </div>

              <div className="space-y-4">
                <div className="bg-error/10 border-l-4 border-error p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="XMarkIcon" size={24} className="text-error flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Limited Real-Time Monitoring</h4>
                      <p className="text-muted-foreground text-sm">No continuous brain activity assessment during critical treatment window</p>
                    </div>
                  </div>
                </div>

                <div className="bg-error/10 border-l-4 border-error p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="XMarkIcon" size={24} className="text-error flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Reactive Intervention</h4>
                      <p className="text-muted-foreground text-sm">Complications detected only after they occur, missing prevention opportunities</p>
                    </div>
                  </div>
                </div>

                <div className="bg-error/10 border-l-4 border-error p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="XMarkIcon" size={24} className="text-error flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Higher Complication Rates</h4>
                      <p className="text-muted-foreground text-sm">32% complication reduction with longer 14-21 day recovery periods</p>
                    </div>
                  </div>
                </div>

                <div className="bg-error/10 border-l-4 border-error p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="XMarkIcon" size={24} className="text-error flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Manual Monitoring Burden</h4>
                      <p className="text-muted-foreground text-sm">Requires constant staff attention with periodic manual checks prone to human error</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl shadow-card p-8 mb-16 monitoring-grid border-2">
          <h3 className="text-2xl font-bold font-heading text-foreground mb-8 text-center">
            Clinical Advantage Comparison
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border nicu-monitor-bg">
                  <th className="text-left py-4 px-4 font-semibold text-primary-foreground">Feature</th>
                  <th className="text-left py-4 px-4 font-semibold text-primary-foreground">Traditional Methods</th>
                  <th className="text-left py-4 px-4 font-semibold text-primary-foreground">REVIVE System</th>
                  <th className="text-left py-4 px-4 font-semibold text-primary-foreground">Advantage</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((item, index) =>
                <tr key={index} className="border-b border-border hover:bg-surface transition-colors duration-250 ease-out">
                    <td className="py-4 px-4 font-medium text-foreground">{item.feature}</td>
                    <td className="py-4 px-4 text-muted-foreground">{item.traditional}</td>
                    <td className="py-4 px-4 text-foreground font-semibold">{item.revive}</td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center space-x-1 bg-nicu-vital/10 text-nicu-vital px-3 py-1 rounded-2xl text-sm font-semibold">
                        <Icon name="ArrowTrendingUpIcon" size={16} />
                        <span>{item.advantage}</span>
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {technologyFeatures.map((feature, index) =>
          <div key={index} className="bg-card rounded-2xl p-6 hover:shadow-card transition-all duration-250 ease-out border border-neuro-accent/20 hover:border-neuro-accent/40">
              <div className="p-3 bg-neuro-primary/10 rounded-2xl inline-flex mb-4">
                <Icon name={feature.icon as any} size={32} className="text-neuro-primary" />
              </div>
              <h4 className="text-lg font-bold font-heading text-foreground mb-3">
                {feature.title}
              </h4>
              <p className="text-muted-foreground">
                {feature.description}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Gradient transition to next section */}
      
    </section>);

};

export default SolutionOverview;